<template>
  <v-container>
    <LoginRequired/>
    <Scanner/>
    <ErrorDialog
        :show="isError"
        :errorContent="errorContent"
    />

    <template v-if="isLoaded">
      <v-alert
          color="primary"
          elevation="24"
          icon="mdi-information-outline"
          type="info"
      >
        Von Lager {{ warehouseFrom }}: {{ selectedBinLocation ? selectedBinLocation.displayName : '...' }} | Nach Lager {{ warehouseTo }}: ...
      </v-alert>

      <v-container class="palette-information-container">
        <v-alert
            color="primary"
            elevation="24"
            outlined
            v-model="showStartInfo"
            @click="toggleInfo"
        >
          <v-icon color="primary">mdi-information-outline</v-icon>
          <p>Scannen oder wählen Sie den Quell-Lagerplatz aus Lager {{ warehouseFrom }}, der anschließend vollständig von {{ warehouseFrom }} nach {{ warehouseTo }} umgelagert wird.</p>
        </v-alert>
        <v-layout justify-center>
          <v-alert
              color="primary"
              elevation="24"
              outlined
              width="60px"
              v-model="showIconOnly"
              @click="toggleInfo"
          >
            <v-icon color="primary">mdi-information-outline</v-icon>
          </v-alert>
        </v-layout>
      </v-container>

      <v-container class="text-align-center">
        <p>{{ selectedBinLocation ? selectedBinLocation.displayName : 'Lagerplatz / Palette wählen' }}</p>
        <v-text-field
            :value="selectedBinLocation ? selectedBinLocation.code : ''"
            label="Lagerplatz / Palette scannen"
            outlined
            readonly
            dense
            ref="scannerResult"
            style="margin: auto"
            prepend-icon="mdi-barcode-scan"
        ></v-text-field>
        <v-btn
            color="success"
            class="ma-2 white--text"
            :disabled="!selectedBinLocation"
            :to="getSelectToRoute()"
        >
          Weiter
        </v-btn>
      </v-container>

      <div v-if="binLocations && binLocations.length > 0">
        <v-text-field
            v-model="search"
            dense
            clearable
            outlined
            prepend-icon="mdi-magnify"
            label="Suche nach Code oder Name"
        ></v-text-field>
        <v-virtual-scroll
            v-if="filteredBinLocations && filteredBinLocations.length > 0"
            :items="filteredBinLocations"
            :item-height="56"
            :height="360"
            class="palette-scroll"
        >
          <template v-slot:default="{ item }">
            <v-list-item :key="item.code" class="palette-list-item">
              <v-btn
                  @click="selectBinLocation(item)"
                  block
                  color="secondary"
                  class="white--text palette-button"
              >
                {{ item.displayName }}
              </v-btn>
            </v-list-item>
          </template>
        </v-virtual-scroll>
        <v-alert
            v-else
            dense
            outlined
            type="info"
        >
          Keine Treffer für die aktuelle Suche.
        </v-alert>
      </div>
      <div v-else>
        <v-alert
            dense
            outlined
            type="error"
        >
          Keine Lagerplätze im {{ warehouseFrom }} gefunden!
        </v-alert>
      </div>
    </template>

    <v-container v-else>
      <v-progress-circular
          indeterminate
          color="primary"
      ></v-progress-circular>
    </v-container>

    <VerticalSpacer/>
    <v-row>
      <v-spacer/>
      <v-btn
          :to="{ name: 'rearrange.entire_bin_location.select_warehouse_from_to' }"
          outlined
          color="primary"
      >
        Zurück
      </v-btn>
      <v-spacer/>
    </v-row>
  </v-container>
</template>

<script>
import VerticalSpacer from "../../Others/VerticalSpacer";
import LoginRequired from "../../Others/LoginRequired";
import Scanner from "../../Others/Scanner";
import ErrorDialog from "../../Others/ErrorDialog";

export default {
  components: {ErrorDialog, Scanner, LoginRequired, VerticalSpacer},
  data: () => ({
    showStartInfo: false,
    showIconOnly: true,
    selectedBinLocation: null,
    warehouseFrom: '',
    warehouseTo: '',
    isLoaded: false,
    isError: false,
    errorContent: '',
    binLocations: [],
    search: '',
  }),
  computed: {
    filteredBinLocations() {
      const query = (this.search || '').trim().toLowerCase();
      if (!query) {
        return this.binLocations;
      }

      return this.binLocations.filter(binLocation => {
        const code = (binLocation.code || '').toLowerCase();
        const displayName = (binLocation.displayName || '').toLowerCase();
        return code.includes(query) || displayName.includes(query);
      });
    },
  },
  created() {
    this.$root.$on('onScannerInput', this.onScannerInputHandler);
    this.$root.$on('onCloseErrorDialog', this.handleCloseErrorDialog);

    this.warehouseFrom = this.$route.params.from;
    this.warehouseTo = this.$route.params.to;

    this.loadData();
  },

  beforeDestroy() {
    this.$root.$off('onScannerInput', this.onScannerInputHandler);
    this.$root.$off('onCloseErrorDialog', this.handleCloseErrorDialog);
  },

  methods: {
    async loadData() {
      this.isLoaded = false;
      try {
        if (!this.warehouseFrom || !this.warehouseTo) {
          throw new Error('Ungültige Lagerauswahl in der URL.');
        }

        this.binLocations = this.sortBinLocationsByDisplayName(await this.loadAllBinLocations(this.warehouseFrom));
      } catch (error) {
        this.setScannerError(error && error.message ? error.message : 'Unbekannter Fehler');
      } finally {
        this.isLoaded = true;
      }
    },
    async loadAllBinLocations(warehouseCode) {
      const limit = 1000;
      let offset = 0;
      let total = 0;
      let loaded = 0;
      const items = [];

      do {
        const response = await this.$axios.get(
            `binLocationSelection?warehouseCode=${warehouseCode}&includePalettes=1&onlyWithStock=1&limit=${limit}&offset=${offset}`
        );
        if (!(response && response.data && response.data.success)) {
          throw new Error((response && response.data && response.data.message) ? response.data.message : 'Lagerplätze konnten nicht geladen werden.');
        }

        const chunk = Array.isArray(response.data.data) ? response.data.data : [];
        total = Number(response.data.total || 0);
        if (chunk.length === 0) {
          break;
        }
        loaded += chunk.length;
        offset += chunk.length;
        items.push(...chunk);
      } while (loaded < total);

      return items;
    },
    sortBinLocationsByDisplayName(binLocations) {
      return [...binLocations].sort((left, right) => {
        const leftLabel = (left && (left.displayName || left.code)) ? String(left.displayName || left.code) : '';
        const rightLabel = (right && (right.displayName || right.code)) ? String(right.displayName || right.code) : '';
        return leftLabel.localeCompare(rightLabel, 'de', {numeric: true, sensitivity: 'base'});
      });
    },
    toggleInfo() {
      this.showStartInfo = !this.showStartInfo;
      this.showIconOnly = !this.showIconOnly;
    },
    selectBinLocation(binLocation) {
      this.isError = false;
      this.selectedBinLocation = binLocation;
    },
    getSelectToRoute() {
      return {
        name: 'rearrange.entire_bin_location.select_bin_location_to',
        params: {
          from: this.warehouseFrom,
          to: this.warehouseTo,
          fromBinLocation: this.selectedBinLocation ? this.selectedBinLocation.code : 'unknown'
        },
        query: {
          fromDisplayName: this.selectedBinLocation ? String(this.selectedBinLocation.displayName || '').trim() : ''
        },
      };
    },
    proceedToSelectTo() {
      if (!this.selectedBinLocation) {
        return;
      }
      this.$router.push(this.getSelectToRoute());
    },
    onScannerInputHandler(scannerInputRaw) {
      if (!this.isLoaded) {
        return;
      }
      const scannerInput = this.parseJson(scannerInputRaw);
      const scannedInput = (scannerInput.dataAsText || '').replace('\n', '').trim();
      const scannedCode = scannedInput.includes('$') ? scannedInput.split('$')[1] : scannedInput;

      if (!scannedCode) {
        this.selectBinLocation(null);
        this.blurScannerField();
        return this.setScannerError(`${scannerInput.dataAsText} ist keine gültige Eingabe für Lagerplatz!`);
      }

      const targetBinLocation = this.binLocations.find(binLocation => binLocation.code === scannedCode);
      if (!targetBinLocation) {
        this.selectBinLocation(null);
        this.blurScannerField();
        return this.setScannerError(`Lagerplatz ${scannedCode} kann nicht gefunden werden oder ist leer!`);
      }

      this.selectBinLocation(targetBinLocation);
      this.blurScannerField();
      this.proceedToSelectTo();
    },
    handleCloseErrorDialog() {
      this.isError = false;
    },
    setScannerError(message) {
      this.isError = true;
      this.errorContent = message;
    },
    parseJson(jsonString) {
      let jsonObj = {
        dataAsText: 'none',
      };
      try {
        jsonObj = JSON.parse(jsonString);
      } catch (e) {
        console.error(e);
      }
      return jsonObj;
    },
    blurScannerField() {
      if (this.$refs.scannerResult && this.$refs.scannerResult.blur) {
        this.$refs.scannerResult.blur();
      }
    },
  },
}
</script>
